// Shared axios instance with refresh-on-401 interceptor.
// Per architecture/frontend-architecture.md + login-flow-spec.md §6.
//
// Behavior:
// - Attaches Authorization: Bearer <access_token> when present.
// - On 401 from any non-/auth/* request: try ONE refresh; on success,
//   replay the original request with the new token; on failure, clear
//   the auth store (UI redirects to /login via the route guard).

import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'

import { authStore } from '../auth/store.js'

interface RetryableConfig extends AxiosRequestConfig {
  _retried?: boolean
}

interface RefreshResponse {
  data: {
    access_token: string
    refresh_token: string
    expires_in: number
  }
}

/**
 * Axios instance for all `/api` calls. Attaches the Bearer access token and, on
 * a 401 outside `/auth/*`, runs ONE shared refresh and replays the request; a
 * failed refresh clears the auth store (route guard then redirects to /login).
 */
export const apiClient = axios.create({
  baseURL: '/api',
})

apiClient.interceptors.request.use((config) => {
  const token = authStore.get().accessToken
  if (token !== null) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Single-flight refresh: when several requests 401 at once (e.g. multiple
// queries fire on the first render after login), they share ONE /auth/refresh
// call. The refresh token rotates on use, so racing refreshes would consume it
// and fail all but the first — leaving those queries stuck (a page that never
// stops loading). Sharing one in-flight refresh fixes that. Resolves to the new
// access token.
let refreshInFlight: Promise<string> | null = null

async function runRefresh(refreshToken: string): Promise<string> {
  const res = await axios.post<RefreshResponse>('/api/auth/refresh', {
    refresh_token: refreshToken,
  })
  const currentUser = authStore.get().user
  if (currentUser === null) throw new Error('refresh: no current user')
  authStore.setSession({
    accessToken: res.data.data.access_token,
    refreshToken: res.data.data.refresh_token,
    user: currentUser,
  })
  return res.data.data.access_token
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined
    const status = error.response?.status
    const url = original?.url ?? ''

    // MFA-enrollment gate: a 403 with auth_mfa_enrollment_required means a
    // mandatory-role user (RBAC §14) must finish enrolling before using
    // role-restricted routes. Send them to /account/security.
    const errorCode = (error.response?.data as { error?: { code?: string } } | undefined)?.error
      ?.code
    if (status === 403 && errorCode === 'auth_mfa_enrollment_required') {
      if (typeof window !== 'undefined' && window.location.pathname !== '/account/security') {
        // ?politica=mfa lets the security page explain WHY the user landed
        // there (checkpoint-2: the silent bounce read as a broken app).
        window.location.assign('/account/security?politica=mfa')
      }
      return Promise.reject(error)
    }

    // Only retry once, only on 401, only for non-/auth/* routes.
    if (
      status !== 401 ||
      original === undefined ||
      original._retried === true ||
      url.startsWith('/auth/')
    ) {
      // A 401 on an already-replayed request means the freshly-minted token
      // was rejected too — the session is revoked server-side. Clear the
      // store so the route guard bounces to /login; without this the revoked
      // device just sits on the page erroring forever.
      if (status === 401 && original?._retried === true) {
        authStore.clear()
      }
      return Promise.reject(error)
    }

    const refreshToken = authStore.get().refreshToken
    if (refreshToken === null) {
      authStore.clear()
      throw error
    }

    try {
      // Share a single refresh across concurrent 401s (see runRefresh above).
      refreshInFlight ??= runRefresh(refreshToken).finally(() => {
        refreshInFlight = null
      })
      const newAccessToken = await refreshInFlight

      // Replay the original request with the new token.
      original._retried = true
      original.headers = {
        ...(original.headers ?? {}),
        Authorization: `Bearer ${newAccessToken}`,
      }
      return apiClient.request(original)
    } catch (refreshErr) {
      authStore.clear()
      throw refreshErr
    }
  },
)

// ─── Typed call helpers for the auth flow ──────────────────────────────────

export interface LoginCompleteResponse {
  data: {
    step: 'complete'
    access_token: string
    refresh_token: string
    expires_in: number
    user: { id: string; email: string; name: string }
  }
}

export interface LoginMfaResponse {
  data: {
    step: 'mfa'
    mfa_challenge_token: string
    expires_in: number
  }
}

export interface LoginPasswordChangeResponse {
  data: {
    step: 'password_change'
    password_change_token: string
    expires_in: number
  }
}

export type LoginResponse = LoginCompleteResponse | LoginMfaResponse | LoginPasswordChangeResponse

/** POST /auth/login. Resolves to step:'complete' (tokens + user) or step:'mfa'
 *  (challenge token for postMfaVerify). Bad credentials reject as an axios
 *  error — /auth/* is exempt from the refresh-retry interceptor. */
export async function postLogin(email: string, password: string): Promise<LoginResponse> {
  const res = await apiClient.post<LoginResponse>('/auth/login', { email, password })
  return res.data
}

export interface MfaVerifyResponse {
  data: {
    access_token: string
    refresh_token: string
    expires_in: number
    user: { id: string; email: string; name: string }
  }
}

/** POST /auth/password-change — a direct-created account trades its temp
 *  password's step:'password_change' token + a NEW password for the full
 *  session pair. */
export async function postForcedPasswordChange(
  passwordChangeToken: string,
  newPassword: string,
): Promise<MfaVerifyResponse> {
  const res = await apiClient.post<MfaVerifyResponse>('/auth/password-change', {
    password_change_token: passwordChangeToken,
    new_password: newPassword,
  })
  return res.data
}

/** POST /auth/mfa/verify — exchanges the step:'mfa' challenge token plus a TOTP
 *  or backup code for the full session tokens. */
export async function postMfaVerify(
  challengeToken: string,
  code: string,
  isBackupCode: boolean,
): Promise<MfaVerifyResponse> {
  const res = await apiClient.post<MfaVerifyResponse>('/auth/mfa/verify', {
    mfa_challenge_token: challengeToken,
    code,
    is_backup_code: isBackupCode,
  })
  return res.data
}

// ─── MFA enrollment (the /account/security page) ───────────────────────────

export interface MfaStatus {
  mfa_enabled: boolean
  mfa_mandatory: boolean
  backup_codes_remaining: number
  mfa_enrolled_at: string | null
}

/** GET /auth/mfa/status — enrollment state feeding the /account/security page. */
export async function getMfaStatus(): Promise<MfaStatus> {
  const res = await apiClient.get<{ data: MfaStatus }>('/auth/mfa/status')
  return res.data.data
}

export interface MfaEnrollStart {
  qr_code_data_url: string
  manual_secret: string
}

/** POST /auth/mfa/enroll/start — begins TOTP enrollment; returns the QR data
 *  URL + manual secret to display. Complete with postMfaEnrollVerify. */
export async function postMfaEnrollStart(): Promise<MfaEnrollStart> {
  const res = await apiClient.post<{ data: MfaEnrollStart }>('/auth/mfa/enroll/start', {})
  return res.data.data
}

/** Completes enrollment; returns the one-time backup codes. */
export interface MfaEnrollComplete {
  backup_codes: readonly string[]
  /** Fresh mfa_verified=true session — apply it so enrolling under the
   *  obligatoria policy restores console access without re-login. */
  access_token: string
  refresh_token: string
}

export async function postMfaEnrollVerify(code: string): Promise<MfaEnrollComplete> {
  const res = await apiClient.post<{ data: MfaEnrollComplete }>('/auth/mfa/enroll/verify', {
    code,
  })
  return res.data.data
}

/** POST /auth/mfa/disable — re-authenticates with the current password plus a
 *  TOTP or backup code before turning MFA off. */
export async function postMfaDisable(
  currentPassword: string,
  code: string,
  isBackupCode: boolean,
): Promise<void> {
  await apiClient.post('/auth/mfa/disable', {
    current_password: currentPassword,
    code,
    is_backup_code: isBackupCode,
  })
}

/** POST /account/password — change the signed-in user's password. Re-auths
 *  with the current password; the server revokes every OTHER session. */
export async function postChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post('/account/password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

/** Regenerates backup codes; returns the fresh one-time set. */
export async function postMfaRegenerateBackupCodes(code: string): Promise<readonly string[]> {
  const res = await apiClient.post<{ data: { backup_codes: string[] } }>(
    '/auth/mfa/backup-codes/regenerate',
    { code },
  )
  return res.data.data.backup_codes
}
